import { useState, useRef } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Upload, FileText, CheckCircle, XCircle, AlertTriangle, Download } from 'lucide-react';

interface Agency {
  id: string;
  name: string;
  code: string;
}

interface ParsedSupervisor {
  email: string;
  password: string;
  isValid: boolean;
  error?: string;
}

interface ImportResult {
  email: string;
  success: boolean;
  error?: string;
}

interface BulkSupervisorImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  agencies: Agency[];
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'results';

export function BulkSupervisorImportDialog({ open, onOpenChange, onSuccess, agencies }: BulkSupervisorImportDialogProps) {
  const { user } = useAdminAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [step, setStep] = useState<ImportStep>('upload');
  const [selectedAgency, setSelectedAgency] = useState<string>(user?.agency_id || '');
  const [parsedData, setParsedData] = useState<ParsedSupervisor[]>([]);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  const resetDialog = () => {
    setStep('upload');
    setParsedData([]);
    setResults([]);
    setProgress(0);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validatePassword = (password: string): boolean => {
    return password.length >= 8;
  };

  const parseCSV = (content: string): ParsedSupervisor[] => {
    const lines = content.split('\n').filter(line => line.trim());
    const results: ParsedSupervisor[] = [];
    
    // Skip header if it looks like one
    const startIndex = lines[0]?.toLowerCase().includes('email') ? 1 : 0;
    
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Handle both comma and semicolon separators
      const separator = line.includes(';') ? ';' : ',';
      const parts = line.split(separator).map(p => p.trim().replace(/^["']|["']$/g, ''));
      
      const email = parts[0] || '';
      const password = parts[1] || '';
      
      let isValid = true;
      let error: string | undefined;
      
      if (!email) {
        isValid = false;
        error = 'Email is required';
      } else if (!validateEmail(email)) {
        isValid = false;
        error = 'Invalid email format';
      } else if (!password) {
        isValid = false;
        error = 'Password is required';
      } else if (!validatePassword(password)) {
        isValid = false;
        error = 'Password must be at least 8 characters';
      }
      
      results.push({ email: email.toLowerCase(), password, isValid, error });
    }
    
    // Check for duplicates
    const seen = new Set<string>();
    for (const row of results) {
      if (row.isValid && seen.has(row.email)) {
        row.isValid = false;
        row.error = 'Duplicate email in file';
      }
      seen.add(row.email);
    }
    
    return results;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const parsed = parseCSV(content);
      
      if (parsed.length === 0) {
        toast({
          title: "Empty file",
          description: "No valid data found in the CSV file",
          variant: "destructive",
        });
        return;
      }
      
      setParsedData(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!selectedAgency) {
      toast({
        title: "Agency required",
        description: "Please select an agency for the supervisors",
        variant: "destructive",
      });
      return;
    }

    const validRows = parsedData.filter(row => row.isValid);
    if (validRows.length === 0) {
      toast({
        title: "No valid entries",
        description: "Please fix the errors before importing",
        variant: "destructive",
      });
      return;
    }

    setStep('importing');
    setIsProcessing(true);
    const importResults: ImportResult[] = [];

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      setProgress(Math.round(((i + 1) / validRows.length) * 100));

      try {
        // Create user via Supabase Auth
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: row.email,
          password: row.password,
          options: {
            emailRedirectTo: window.location.origin,
            data: { role: 'supervisor' }
          }
        });

        if (authError) {
          importResults.push({ email: row.email, success: false, error: authError.message });
          continue;
        }

        if (!authData.user) {
          importResults.push({ email: row.email, success: false, error: 'Failed to create user' });
          continue;
        }

        const userId = authData.user.id;

        // Wait for trigger to complete
        await new Promise(resolve => setTimeout(resolve, 300));

        // Update profile with correct agency_id
        await supabase
          .from('profiles')
          .update({ agency_id: selectedAgency, role: 'agency_admin' as const })
          .eq('user_id', userId);

        // Delete the auto-created patient role
        await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', userId)
          .eq('role', 'patient');

        // Assign supervisor role
        const { error: roleError } = await supabase
          .from('user_roles')
          .upsert({
            user_id: userId,
            role: 'supervisor',
            assigned_by: user?.id
          }, { onConflict: 'user_id,role' });

        if (roleError) {
          importResults.push({ email: row.email, success: false, error: 'Failed to assign role' });
          continue;
        }

        importResults.push({ email: row.email, success: true });

      } catch (error: any) {
        importResults.push({ email: row.email, success: false, error: error.message || 'Unknown error' });
      }
    }

    setResults(importResults);
    setStep('results');
    setIsProcessing(false);

    const successCount = importResults.filter(r => r.success).length;
    if (successCount > 0) {
      onSuccess();
    }
  };

  const downloadTemplate = () => {
    const template = 'email,password\nexample1@email.com,password123\nexample2@email.com,securepass456';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'supervisor_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const validCount = parsedData.filter(r => r.isValid).length;
  const invalidCount = parsedData.filter(r => !r.isValid).length;
  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Bulk Import Supervisors
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Upload a CSV file with supervisor emails and passwords.'}
            {step === 'preview' && 'Review the data before importing.'}
            {step === 'importing' && 'Creating supervisor accounts...'}
            {step === 'results' && 'Import completed.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            {/* Agency Selection */}
            <div className="space-y-2">
              <Label>Agency *</Label>
              <Select
                value={selectedAgency}
                onValueChange={setSelectedAgency}
                disabled={!isSuperAdmin && !!user?.agency_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an agency" />
                </SelectTrigger>
                <SelectContent>
                  {agencies.map(agency => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name} ({agency.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>CSV File *</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-3">
                  Upload a CSV file with columns: <code className="bg-muted px-1 rounded">email,password</code>
                </p>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="max-w-xs mx-auto"
                />
              </div>
            </div>

            {/* Template Download */}
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>Need a template? Download our CSV template.</span>
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-1" />
                  Template
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex gap-3">
              <Badge variant="secondary" className="gap-1">
                <CheckCircle className="h-3 w-3 text-green-600" />
                {validCount} valid
              </Badge>
              {invalidCount > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <XCircle className="h-3 w-3" />
                  {invalidCount} invalid
                </Badge>
              )}
            </div>

            {/* Preview Table */}
            <ScrollArea className="h-[300px] border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Password</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedData.map((row, idx) => (
                    <TableRow key={idx} className={!row.isValid ? 'bg-destructive/10' : ''}>
                      <TableCell>
                        {row.isValid ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-destructive" />
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{row.email || '-'}</TableCell>
                      <TableCell className="font-mono text-sm">{'•'.repeat(Math.min(row.password.length, 8))}</TableCell>
                      <TableCell className="text-sm text-destructive">{row.error || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            {invalidCount > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {invalidCount} row(s) have errors and will be skipped during import.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {step === 'importing' && (
          <div className="space-y-4 py-8">
            <div className="text-center">
              <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-lg font-medium">Importing supervisors...</p>
              <p className="text-sm text-muted-foreground">Please wait, this may take a moment.</p>
            </div>
            <Progress value={progress} className="w-full" />
            <p className="text-center text-sm text-muted-foreground">{progress}% complete</p>
          </div>
        )}

        {step === 'results' && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex gap-3">
              <Badge className="gap-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                <CheckCircle className="h-3 w-3" />
                {successCount} created
              </Badge>
              {failCount > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <XCircle className="h-3 w-3" />
                  {failCount} failed
                </Badge>
              )}
            </div>

            {/* Results Table */}
            <ScrollArea className="h-[300px] border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((row, idx) => (
                    <TableRow key={idx} className={!row.success ? 'bg-destructive/10' : ''}>
                      <TableCell>
                        {row.success ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-destructive" />
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{row.email}</TableCell>
                      <TableCell className="text-sm text-destructive">{row.error || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            {successCount > 0 && (
              <Alert>
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  {successCount} supervisor(s) have been created. They should check their email to confirm their accounts.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && (
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => { resetDialog(); }}>
                Back
              </Button>
              <Button onClick={handleImport} disabled={validCount === 0}>
                Import {validCount} Supervisor{validCount !== 1 ? 's' : ''}
              </Button>
            </>
          )}
          {step === 'results' && (
            <Button onClick={handleClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
